import {
    PermissionCheckerService,
    FeatureCheckerService,
    LocalizationService,
    MessageService,
    AbpMultiTenancyService,
    NotifyService,
    SettingService
} from 'abp-ng2-module';
import { ChangeDetectorRef, Injector } from '@angular/core';
import { AppConsts } from '@shared/AppConsts';
import { AppUrlService } from '@shared/common/nav/app-url.service';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { AppUiCustomizationService } from '@shared/common/ui/app-ui-customization.service';
import { PrimengTableHelper } from '@shared/helpers/PrimengTableHelper';
import { UiCustomizationSettingsDto } from '@shared/service-proxies/service-proxies';
import { requestApiRenderFlush } from '@shared/service-proxies/api-render-flush.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { NgxSpinnerTextService } from '@app/shared/ngx-spinner-text.service';
import { DateTime } from 'luxon';
import { HeaderTitleService } from '@app/shared/common/header-title/header-title.service';
import { appLog } from '@shared/common/logging/app-log.util';
import {
    trackByCode,
    trackById,
    trackByIdentity,
    trackByIndex,
    trackByKey,
    trackByName,
    trackByProjectId,
    trackByProjectTaskId,
    trackByTaskId,
    trackByTenantId,
    trackByUserId,
    trackByValue,
} from '@shared/common/track-by.util';

export abstract class AppComponentBase {

    localizationSourceName = AppConsts.localization.defaultLocalizationSourceName;

    localization: LocalizationService;
    permission: PermissionCheckerService;
    feature: FeatureCheckerService;
    notify: NotifyService;
    setting: SettingService;
    message: MessageService;
    multiTenancy: AbpMultiTenancyService;
    appSession: AppSessionService;
    primengTableHelper: PrimengTableHelper;
    ui: AppUiCustomizationService;
    appUrlService: AppUrlService;
    spinnerService: NgxSpinnerService;
    private ngxSpinnerTextService: NgxSpinnerTextService;
    _headerTitleService: HeaderTitleService;
    /** Centralized logger — routes to ABP, App Insights, and dev console. */
    protected readonly appLog = appLog;
    protected readonly changeDetectorRef: ChangeDetectorRef | null;

    constructor(injector: Injector) {
        this.localization = injector.get(LocalizationService);
        this.permission = injector.get(PermissionCheckerService);
        this.feature = injector.get(FeatureCheckerService);
        this.notify = injector.get(NotifyService);
        this.setting = injector.get(SettingService);
        this.message = injector.get(MessageService);
        this.multiTenancy = injector.get(AbpMultiTenancyService);
        this.appSession = injector.get(AppSessionService);
        this.ui = injector.get(AppUiCustomizationService);
        this.appUrlService = injector.get(AppUrlService);
        this.primengTableHelper = new PrimengTableHelper();
        this.spinnerService = injector.get(NgxSpinnerService);
        this.ngxSpinnerTextService = injector.get(NgxSpinnerTextService);
        this._headerTitleService = injector.get(HeaderTitleService);
        this.changeDetectorRef = injector.get(ChangeDetectorRef, null, { optional: true });
    }

    /** Notify Angular to check this component when using OnPush change detection. */
    protected markForCheck(): void {
        this.changeDetectorRef?.markForCheck();
        requestApiRenderFlush();
    }

    readonly trackByIndex = trackByIndex;
    readonly trackByIdentity = trackByIdentity;
    readonly trackById = trackById;
    readonly trackByUserId = trackByUserId;
    readonly trackByProjectId = trackByProjectId;
    readonly trackByTaskId = trackByTaskId;
    readonly trackByProjectTaskId = trackByProjectTaskId;
    readonly trackByTenantId = trackByTenantId;
    readonly trackByKey = trackByKey;
    readonly trackByCode = trackByCode;
    readonly trackByName = trackByName;
    readonly trackByValue = trackByValue;

    flattenDeep(array) {
        return array.reduce((acc, val) =>
            Array.isArray(val) ?
                acc.concat(this.flattenDeep(val)) :
                acc.concat(val),
            []);
    }

    l(key: string, ...args: any[]): string {
        args.unshift(key);
        args.unshift(this.localizationSourceName);
        return this.ls.apply(this, args);
    }

    ls(sourcename: string, key: string, ...args: any[]): string {
        let localizedText = this.localization.localize(key, sourcename);

        if (!localizedText) {
            localizedText = key;
        }

        if (!args || !args.length) {
            return localizedText;
        }

        args.unshift(localizedText);
        return abp.utils.formatString.apply(this, this.flattenDeep(args));
    }

    isGranted(permissionName: string): boolean {
        return this.permission.isGranted(permissionName);
    }

    isGrantedAny(...permissions: string[]): boolean {
        if (!permissions) {
            return false;
        }

        for (const permission of permissions) {
            if (this.isGranted(permission)) {
                return true;
            }
        }

        return false;
    }

    s(key: string): string {
        return abp.setting.get(key);
    }

    appRootUrl(): string {
        return this.appUrlService.appRootUrl;
    }

    get currentTheme(): UiCustomizationSettingsDto {
        return this.appSession.theme;
    }

    get containerClass(): string {
        const layoutType = this.appSession.theme.baseSettings.layout.layoutType;
        if (layoutType === 'fluid' || layoutType === 'fluid-xxl') {
            return 'container-fluid';
        }

        return 'container';
    }

    showMainSpinner(text?: string): void {
        this.ngxSpinnerTextService.setText(text);
        this.spinnerService.show();
    }

    hideMainSpinner(text?: string): void {
        this.spinnerService.hide();
    }
    getEnumValues(enumObj: any) {
        let map: { id: number; name: string }[] = [];
        for (let n in enumObj) {
            if (typeof enumObj[n] === 'number') {
                map.push({ id: <any>enumObj[n], name: this.l(n) });
            }
        }
        map = map.sort((a, b) => a.name.localeCompare(b.name));
        return map;
    }
    getDateTimeFromJSDate(date: any) {
    
        if ((date instanceof Date)) {
             let year = date.getFullYear();
             let month = Number(date.getMonth() + 1);
             let day = Number(("0" + date.getDate()).slice(-2));
             let hours = date.getHours();
             let minutes = date.getMinutes();
             let second = date.getSeconds();
             return DateTime.local(year, month, day, hours, minutes, second);
        }
        return date;
    }
}
